import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaInstagram } from 'react-icons/fa';
import Botpoison from '@botpoison/browser';

interface ContactFormProps {
  location: {
    address: string;
    fullAddress: string;
    phone: string;
    email: string;
    instagram: string;
    instagramHandle: string;
    mapEmbedUrl: string;
    name: string;
  };
  formsparkId: string;
  botpoisonKey: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ location, formsparkId, botpoisonKey }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create Botpoison instance with the provided key
      const botpoison = new Botpoison({
        publicKey: botpoisonKey
      });

      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await fetch(`https://submit-form.com/${formsparkId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _botpoison: solution
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-container">
        <div className="contact-content">
          {/* Contact Form */}
          <div className="contact-form-container">
            <h2>Contact Us</h2>
            <p>Get in touch with our {location.name} location</p>

            {submitStatus === 'success' && (
              <div className="success-message">
                Thank you for your message! We'll get back to you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                Sorry, there was an error sending your message. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Your email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Tell us about your appointment needs or questions"
                />
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Map and Contact Info */}
          <div className="contact-info-container">
            <div className="map-container">
              <iframe
                src={location.mapEmbedUrl}
                title={`Metropolitan Barbershop ${location.name} Location`}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className="contact-info">
              <div className="contact-item">
                {React.createElement(FaMapMarkerAlt as React.ComponentType)}
                <div>
                  <h4>Address</h4>
                  <p>{location.fullAddress}</p>
                </div>
              </div>

              <div className="contact-item">
                {React.createElement(FaPhone as React.ComponentType)}
                <div>
                  <h4>Phone</h4>
                  <p>{location.phone}</p>
                </div>
              </div>

              <div className="contact-item">
                {React.createElement(FaEnvelope as React.ComponentType)}
                <div>
                  <h4>Email</h4>
                  <p>{location.email}</p>
                </div>
              </div>

              <div className="contact-item">
                {React.createElement(FaInstagram as React.ComponentType)}
                <div>
                  <h4>Follow Us</h4>
                  <a
                    href={location.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="instagram-link"
                  >
                    {location.instagramHandle}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm; 